import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Menu, X, Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CV_URL, LOGO } from "@/data/content";

const FlagBtn = ({ code, active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`h-7 w-7 rounded-full overflow-hidden border-2 transition-all duration-300 ${active ? "border-brand scale-110" : "border-white/25 opacity-55 hover:opacity-100"}`}
    aria-label={code}
  >
    <img src={`https://flagcdn.com/w40/${code}.png`} alt={code} className="h-full w-full object-cover" />
  </button>
);

export const Nav = ({ onOpenReel }) => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "hero", label: t.nav.home },
    { id: "services", label: t.nav.services },
    { id: "about", label: t.nav.about },
    { id: "work", label: t.nav.work },
    { id: "education", label: t.nav.education },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const reel = () => { setOpen(false); onOpenReel(); };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 w-full z-[100] bg-ink transition-shadow duration-300 ${scrolled ? "shadow-lg shadow-black/20" : ""}`}
    >
      <div className="max-w-[88rem] mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between gap-4">
        <button data-testid="nav-logo" onClick={() => go("hero")} className="flex items-center shrink-0">
          <img src={LOGO} alt="Said Kalil" className="h-9 md:h-11 w-auto object-contain" />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-semibold text-sm text-white/85 hover:text-brand transition-colors duration-300 uppercase tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-reel"
            onClick={reel}
            className="flex items-center gap-2 font-semibold text-sm text-white/85 hover:text-brand transition-colors duration-300 uppercase tracking-wide"
          >
            {t.nav.reel}
            <span className="h-6 w-6 rounded-full bg-brand text-ink flex items-center justify-center">
              <Play size={12} className="ml-0.5 fill-ink" />
            </span>
          </button>
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2">
            <FlagBtn code="es" active={lang === "es"} onClick={() => setLang("es")} testid="lang-es" />
            <FlagBtn code="us" active={lang === "en"} onClick={() => setLang("en")} testid="lang-en" />
          </div>
          <a
            data-testid="cv-download-btn"
            href={CV_URL}
            download
            className="hidden sm:inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 bg-brand text-ink hover:bg-white transition-colors duration-300"
          >
            <Download size={15} /> {t.nav.cv}
          </a>
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="lg:hidden text-white">
            <Menu size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink z-[110] flex flex-col p-7 lg:hidden"
          >
            <div className="flex justify-between items-center">
              <img src={LOGO} alt="Said Kalil" className="h-9 w-auto object-contain" />
              <button data-testid="menu-close" onClick={() => setOpen(false)} className="text-white"><X size={28} /></button>
            </div>
            <div className="flex flex-col gap-5 mt-14">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.05 }}
                  onClick={() => go(l.id)}
                  className="font-extrabold text-4xl text-left text-white uppercase"
                >
                  {l.label}
                </motion.button>
              ))}
              <button onClick={reel} className="flex items-center gap-3 font-extrabold text-4xl text-brand uppercase">
                {t.nav.reel} <Play size={26} className="fill-brand" />
              </button>
              <a href={CV_URL} download className="mt-4 inline-flex items-center gap-2 font-bold text-base bg-brand text-ink rounded-full px-6 py-3 w-fit">
                <Download size={18} /> {t.nav.cv}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
